import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTasks } from '../../hooks/useTasks';
import { useAuth } from '../../contexts/AuthContext';
import TaskCard from './TaskCard';
import AddTaskModal from './AddTaskModal';
import EditTaskModal from './EditTaskModal';
import TaskSettings from './TaskSettings';
import TaskStats from './TaskStats';
import { Plus, Filter, Settings, CheckSquare, ChevronLeft, ChevronRight, Search, X, SlidersHorizontal } from 'lucide-react';

export default function TodoList() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    tasks,
    loading,
    stats,
    streaks,
    achievements,
    createTask,
    updateTask,
    deleteTask,
    completeTask,
    dismissTask,
    snoozeTask,
    refreshAll
  } = useTasks();

  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'completed'
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const tasksPerPage = 8;
  const [filterBy, setFilterBy] = useState('manual'); // 'all', 'smart', 'manual' - Default to manual (user-created tasks)
  const [highlightedTaskId, setHighlightedTaskId] = useState(null);

  // Check for action parameter to open add modal
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'add') {
      setShowAddModal(true);
      // Remove the action parameter from URL
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Check for highlight parameter to highlight specific task
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId) {
      setHighlightedTaskId(highlightId);
      // Auto-remove highlight after 5 seconds
      const timer = setTimeout(() => {
        setHighlightedTaskId(null);
        // Remove highlight parameter from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('highlight');
        setSearchParams(newParams, { replace: true });
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [searchParams, setSearchParams]);
  
  // Advanced filter states
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('priority'); // 'priority', 'due_date', 'created_at', 'title'
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc', 'desc'
  
  // Filter selections
  const [filters, setFilters] = useState({
    sources: [], // ['auto_generated', 'manual']
    statuses: [], // ['not_started', 'in_progress', 'completed']
    priorities: [], // ['high', 'medium', 'low']
    dueDates: [], // ['today', 'this_week', 'this_month', 'overdue', 'no_date']
    types: [] // ['daily_engagement', 'invoice_related', 'custom']
  });

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterBy, activeTab, filters, searchQuery, sortBy, sortOrder]);

  // Helper functions for filtering
  const getTaskType = (task) => {
    if (task.category === 'daily_engagement') return 'daily_engagement';
    if (task.invoice_id) return 'invoice_related';
    return 'custom';
  };

  const getDueDateCategory = (task) => {
    if (!task.due_date) return 'no_date';
    
    const dueDate = new Date(task.due_date);
    const today = new Date();
    
    // Set today to start of day for accurate comparison
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);

    if (dueDate < today) return 'overdue';
    if (dueDate.getTime() === today.getTime()) return 'today';
    if (dueDate <= nextWeek) return 'this_week';
    if (dueDate <= nextMonth) return 'this_month';
    return 'no_date';
  };

  const applyAdvancedFilters = (taskList) => {
    return taskList.filter(task => {
      // Search query filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesSearch = task.title.toLowerCase().includes(searchLower) ||
                            (task.description && task.description.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }

      // Source filter
      if (filters.sources.length > 0 && !filters.sources.includes(task.source)) {
        return false;
      }

      // Status filter
      if (filters.statuses.length > 0 && !filters.statuses.includes(task.status)) {
        return false;
      }

      // Priority filter
      if (filters.priorities.length > 0 && !filters.priorities.includes(task.priority)) {
        return false;
      }

      // Due date filter
      if (filters.dueDates.length > 0) {
        const dueDateCategory = getDueDateCategory(task);
        if (!filters.dueDates.includes(dueDateCategory)) {
          return false;
        }
      }

      // Type filter
      if (filters.types.length > 0) {
        const taskType = getTaskType(task);
        if (!filters.types.includes(taskType)) {
          return false;
        }
      }

      return true;
    });
  };

  const sortTasks = (taskList) => {
    return [...taskList].sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case 'priority':
          const priorityOrder = { high: 1, medium: 2, low: 3 };
          aValue = priorityOrder[a.priority] || 4;
          bValue = priorityOrder[b.priority] || 4;
          break;
        case 'due_date':
          aValue = a.due_date ? new Date(a.due_date) : new Date('2099-12-31');
          bValue = b.due_date ? new Date(b.due_date) : new Date('2099-12-31');
          break;
        case 'created_at':
          aValue = new Date(a.created_at);
          bValue = new Date(b.created_at);
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        default:
          return 0;
      }

      if (sortOrder === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
  };

  const updateFilter = (category, value) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(item => item !== value)
        : [...prev[category], value]
    }));
  };

  const clearAllFilters = () => {
    setFilters({
      sources: [],
      statuses: [],
      priorities: [],
      dueDates: [],
      types: []
    });
    setSearchQuery('');
    setSortBy('priority');
    setSortOrder('asc');
  };

  const getActiveFilterCount = () => {
    return Object.values(filters).flat().length + (searchQuery ? 1 : 0);
  };

  // Group tasks by priority/category
  const groupTasks = () => {
    // Safety check for tasks array
    if (!tasks || !Array.isArray(tasks)) {
      console.warn('Tasks is not an array:', tasks);
      return { allActiveTasks: [], completed: [] };
    }

    const activeTasks = tasks.filter(t => 
      t.status === 'not_started' || t.status === 'in_progress'
    );

    const completedTasks = tasks.filter(t => 
      t.status === 'completed' && t.archived_at === null
    );

    // Apply basic filter first
    let filtered = activeTab === 'active' ? activeTasks : completedTasks;
    
    if (filterBy === 'smart') {
      filtered = filtered.filter(t => t.source === 'auto_generated');
    } else if (filterBy === 'manual') {
      filtered = filtered.filter(t => t.source === 'manual');
    }

    // Apply advanced filters
    const advancedFiltered = applyAdvancedFilters(filtered);
    
    // Sort the tasks
    const sortedTasks = sortTasks(advancedFiltered);

    if (activeTab === 'completed') {
      return { 
        completed: sortedTasks,
        allActiveTasks: [] 
      };
    }

    return {
      allActiveTasks: sortedTasks,
      completed: []
    };
  };

  // Get total counts for header (without filters)
  const getTotalCounts = () => {
    if (!tasks || !Array.isArray(tasks)) {
      return { activeCount: 0, completedCount: 0 };
    }

    const activeCount = tasks.filter(t => 
      t.status === 'not_started' || t.status === 'in_progress'
    ).length;

    const completedCount = tasks.filter(t => 
      t.status === 'completed' && t.archived_at === null
    ).length;

    return { activeCount, completedCount };
  };

  // Pagination logic
  const getPaginatedTasks = () => {
    const { allActiveTasks = [] } = groupTasks();
    const startIndex = (currentPage - 1) * tasksPerPage;
    const endIndex = startIndex + tasksPerPage;
    return allActiveTasks.slice(startIndex, endIndex);
  };

  const getTotalPages = () => {
    const { allActiveTasks = [] } = groupTasks();
    return Math.ceil(allActiveTasks.length / tasksPerPage);
  };

  const groupedTasks = groupTasks();
  
  // Debug logging
  console.log('Debug - Total tasks:', tasks?.length || 0);
  console.log('Debug - Active tasks:', tasks?.filter(t => t.status === 'not_started' || t.status === 'in_progress').length || 0);
  console.log('Debug - Filtered tasks:', groupedTasks.allActiveTasks?.length || 0);
  console.log('Debug - Current page:', currentPage);
  console.log('Debug - Paginated tasks:', getPaginatedTasks().length);

  // Handle task completion
  const handleComplete = async (taskId) => {
    const result = await completeTask(taskId);
    if (result.success) {
      window.dispatchEvent(new CustomEvent('taskCompleted'));
    }
  };

  // Handle task dismiss
  const handleDismiss = async (taskId) => {
    await dismissTask(taskId);
  };

  // Handle task snooze
  const handleSnooze = async (taskId, hours) => {
    const snoozeUntil = new Date();
    snoozeUntil.setHours(snoozeUntil.getHours() + hours);
    await snoozeTask(taskId, snoozeUntil.toISOString());
  };

  // Handle task delete
  const handleDelete = async (taskId) => {
    if (confirm('Are you sure you want to delete this task?')) {
      await deleteTask(taskId);
    }
  };

  // Handle task edit
  const handleEdit = (task) => {
    setEditingTask(task);
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updates) => {
    if (editingTask) {
      await updateTask(editingTask.id, updates);
      setEditingTask(null);
      setShowEditModal(false);
    }
  };

  // Handle status change
  const handleStatusChange = async (taskId, newStatus) => {
    await updateTask(taskId, { status: newStatus });
  };

  // Safety check for tasks
  if (tasks === undefined || tasks === null) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading tasks...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Single Header Line with Navigation */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">📋 To-Do List</h1>
              <p className="mt-1 text-sm text-gray-600">
                Your smart assistant for invoice management
              </p>
            </div>
            <div className="flex items-center space-x-3">
              {/* Navigation Tabs */}
              <div className="flex space-x-1">
                <button
                  onClick={() => setActiveTab('active')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === 'active'
                      ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 shadow-sm'
                      : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100'
                  }`}
                >
                  Active ({getTotalCounts().activeCount})
                </button>
                <button
                  onClick={() => setActiveTab('completed')}
                  className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeTab === 'completed'
                      ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-700 shadow-sm'
                      : 'text-gray-600 hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100'
                  }`}
                >
                  Completed ({getTotalCounts().completedCount})
                </button>
              </div>
              
              {/* Action Buttons */}
              <button
                onClick={() => setShowSettings(true)}
                className="px-3 py-1 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
              >
                ⚙️ Settings
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="px-3 py-1 text-sm bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-sm hover:shadow-md"
              >
                + Add Task
              </button>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="rounded-lg border border-gray-200 p-4 mb-6 shadow-sm" style={{backgroundColor: '#f9fafb'}}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Search Bar */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-72"
                />
              </div>

              {/* Show Filter */}
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Show:</label>
                <select
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="all">All Tasks</option>
                  <option value="smart">Smart Tasks</option>
                  <option value="manual">Manual Tasks</option>
                </select>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Advanced Filters Button */}
              <button
                onClick={() => setShowFilterPanel(!showFilterPanel)}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center space-x-2 ${
                  showFilterPanel || getActiveFilterCount() > 0
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100'
                }`}
              >
                <SlidersHorizontal className="h-4 w-4" />
                <span>Filters</span>
                {getActiveFilterCount() > 0 && (
                  <span className="bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {getActiveFilterCount()}
                  </span>
                )}
              </button>

              {/* Clear Filters */}
              {getActiveFilterCount() > 0 && (
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1 font-medium"
                >
                  <X className="h-4 w-4" />
                  <span>Clear All</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Advanced Filter Panel */}
        {showFilterPanel && (
          <div className="mb-6 bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Advanced Filters</h3>
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center space-x-1"
              >
                <X className="h-4 w-4" />
                <span>Clear All</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {/* Task Source Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Task Source</label>
                <div className="space-y-2">
                  {[
                    { value: 'auto_generated', label: 'Smart Tasks', count: tasks.filter(t => t.source === 'auto_generated').length },
                    { value: 'manual', label: 'Manual Tasks', count: tasks.filter(t => t.source === 'manual').length }
                  ].map(option => (
                    <label key={option.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.sources.includes(option.value)}
                        onChange={() => updateFilter('sources', option.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{option.label} ({option.count})</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <div className="space-y-2">
                  {[
                    { value: 'not_started', label: 'Not Started', count: tasks.filter(t => t.status === 'not_started').length },
                    { value: 'in_progress', label: 'In Progress', count: tasks.filter(t => t.status === 'in_progress').length },
                    { value: 'completed', label: 'Completed', count: tasks.filter(t => t.status === 'completed').length }
                  ].map(option => (
                    <label key={option.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.statuses.includes(option.value)}
                        onChange={() => updateFilter('statuses', option.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{option.label} ({option.count})</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                <div className="space-y-2">
                  {[
                    { value: 'high', label: 'High Priority', count: tasks.filter(t => t.priority === 'high').length },
                    { value: 'medium', label: 'Medium Priority', count: tasks.filter(t => t.priority === 'medium').length },
                    { value: 'low', label: 'Low Priority', count: tasks.filter(t => t.priority === 'low').length }
                  ].map(option => (
                    <label key={option.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.priorities.includes(option.value)}
                        onChange={() => updateFilter('priorities', option.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{option.label} ({option.count})</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Due Date Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Due Date</label>
                <div className="space-y-2">
                  {[
                    { value: 'overdue', label: 'Overdue', count: tasks.filter(t => getDueDateCategory(t) === 'overdue').length },
                    { value: 'today', label: 'Due Today', count: tasks.filter(t => getDueDateCategory(t) === 'today').length },
                    { value: 'this_week', label: 'This Week', count: tasks.filter(t => getDueDateCategory(t) === 'this_week').length },
                    { value: 'this_month', label: 'This Month', count: tasks.filter(t => getDueDateCategory(t) === 'this_month').length },
                    { value: 'no_date', label: 'No Due Date', count: tasks.filter(t => !t.due_date).length }
                  ].map(option => (
                    <label key={option.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.dueDates.includes(option.value)}
                        onChange={() => updateFilter('dueDates', option.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{option.label} ({option.count})</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Task Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Task Type</label>
                <div className="space-y-2">
                  {[
                    { value: 'daily_engagement', label: 'Daily Engagement', count: tasks.filter(t => t.category === 'daily_engagement').length },
                    { value: 'invoice_related', label: 'Invoice Related', count: tasks.filter(t => t.invoice_id).length },
                    { value: 'custom', label: 'Custom Tasks', count: tasks.filter(t => !t.invoice_id && t.category !== 'daily_engagement').length }
                  ].map(option => (
                    <label key={option.value} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={filters.types.includes(option.value)}
                        onChange={() => updateFilter('types', option.value)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{option.label} ({option.count})</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {/* Sort Options */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">Sort by:</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="priority">Priority</option>
                  <option value="due_date">Due Date</option>
                  <option value="created_at">Created Date</option>
                  <option value="title">Title</option>
                </select>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="px-3 py-1 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="asc">Ascending</option>
                  <option value="desc">Descending</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        )}

        {/* Two-Column Layout with Pagination */}
        {!loading && activeTab === 'active' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Task List */}
            <div className="md:col-span-1">
              {/* Paginated Tasks */}
              {getPaginatedTasks().length > 0 ? (
                <div className="space-y-4">
                  {getPaginatedTasks().map(task => (
                    <div key={task.id} className="rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300" style={{backgroundColor: '#f9fafb'}}>
                      <TaskCard
                        task={task}
                        onComplete={() => handleComplete(task.id)}
                        onDismiss={() => handleDismiss(task.id)}
                        onSnooze={(hours) => handleSnooze(task.id, hours)}
                        onDelete={() => handleDelete(task.id)}
                        isHighlighted={String(highlightedTaskId) === String(task.id)}
                        onEdit={() => handleEdit(task)}
                        onStatusChange={handleStatusChange}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                /* Empty State */
                <div className="rounded-lg shadow-sm border border-gray-200 text-center py-12 px-6" style={{backgroundColor: '#f9fafb'}}>
                  <div className="text-6xl mb-4">🎉</div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2">
                    All caught up!
                  </h3>
                  <p className="text-xs text-gray-600 mb-4">
                    You have no pending tasks. Great work!
                  </p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    + Add New Task
                  </button>
                </div>
              )}

              {/* Pagination Controls */}
              {getTotalPages() > 1 && (
                <div className="flex items-center justify-between mt-6 bg-gradient-to-r from-white to-gray-50 rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * tasksPerPage) + 1} to {Math.min(currentPage * tasksPerPage, groupedTasks.allActiveTasks.length)} of {groupedTasks.allActiveTasks.length} tasks
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-gray-600 font-medium">
                      Page {currentPage} of {getTotalPages()}
                    </span>
                    <button
                      onClick={() => setCurrentPage(Math.min(getTotalPages(), currentPage + 1))}
                      disabled={currentPage === getTotalPages()}
                      className="p-2 rounded-lg border border-gray-300 hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column - Task Stats (Always Visible) */}
            <div className="md:col-span-1">
              <div className="rounded-lg shadow-sm border border-gray-200 p-6" style={{backgroundColor: '#f9fafb'}}>
                <h3 className="text-sm font-semibold text-gray-800 mb-4">Task Statistics</h3>
                {stats ? (
                  <TaskStats stats={stats} completedTasks={groupedTasks.completed} />
                ) : (
                  <div>
                    <p className="text-gray-600 mb-4">Loading stats...</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Completed Tasks - List Layout */}
        {!loading && activeTab === 'completed' && (
          <div className="space-y-6">
            {/* Stats Card */}
            {stats && parseInt(stats.completed_today) > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-green-800 mb-2">
                      ✅ COMPLETED TODAY ({parseInt(stats.completed_today)}) - Great work! 🎉
                    </h3>
                    <p className="text-green-700">
                      You've been productive today! Keep up the momentum.
                    </p>
                  </div>
                  {streaks.find(s => s.streak_type === 'daily_checkin') && (
                    <div className="text-right">
                      <div className="text-3xl font-bold text-green-800">
                        🔥 {streaks.find(s => s.streak_type === 'daily_checkin').current_count}
                      </div>
                      <div className="text-sm text-green-600">
                        Day Streak
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Completed Tasks List */}
            {groupedTasks.completed && groupedTasks.completed.length > 0 ? (
              <div className="space-y-4">
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4">
                  <h3 className="text-xs font-semibold text-green-700 flex items-center">
                    ✅ Completed Tasks ({groupedTasks.completed.length})
                  </h3>
                </div>
                {groupedTasks.completed.map(task => (
                  <TaskCard
                    key={task.id}
                    task={task}
                    onComplete={() => handleComplete(task.id)}
                    onDismiss={() => handleDismiss(task.id)}
                    onSnooze={(hours) => handleSnooze(task.id, hours)}
                    isHighlighted={String(highlightedTaskId) === String(task.id)}
                    onDelete={() => handleDelete(task.id)}
                    onEdit={() => handleEdit(task)}
                    onStatusChange={handleStatusChange}
                  />
                ))}
              </div>
            ) : (
              <div className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-sm border border-gray-200">
                <div className="text-center py-12 px-6">
                  <div className="text-6xl mb-4">🎯</div>
                  <h3 className="text-sm font-bold text-gray-900 mb-2">
                    No completed tasks yet
                  </h3>
                  <p className="text-xs text-gray-600">
                    Complete your first task to see it here!
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddTaskModal
          onClose={() => setShowAddModal(false)}
          onCreate={createTask}
        />
      )}

      {showEditModal && editingTask && (
        <EditTaskModal
          task={editingTask}
          onClose={() => {
            setShowEditModal(false);
            setEditingTask(null);
          }}
          onSave={handleSaveEdit}
        />
      )}

      {showSettings && (
        <TaskSettings
          onClose={() => setShowSettings(false)}
        />
      )}
    </div>
  );
}

